import * as React from 'react';
import * as styles from './report.module.scss';
import Paths from '../../models/enums/paths';
import Breadcrumbs from '../common/breadcrumbs';
import Breadcrumb from '../../models/enums/breadcrumb';
import BreadcrumbOptions from '../../models/enums/breadcrumbOptions';
import InfoIcon from '@material-ui/icons/Info';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import { RouteComponentProps, useParams, useHistory } from 'react-router';
import TableauReport from './tableau-report';
import TableauButton from './tableau-button';
import TableauTool from '../../models/enums/tableauTool';
import useReports from '../../hooks/useReports';
import useTableau from '../../hooks/useTableau';
import { TableauToken } from '../dashboard/dashboard-view';

interface ReportProps extends RouteComponentProps {
  clientId: number;
}

/**
 * @function InfoTooltip - Tooltip that displays last updated information
 */
const InfoTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: 'white',
    color: 'black',
    borderRadius: '4px',
    boxShadow: '0 2px 3px 1px rgba(0, 0, 0, 0.25)',
    maxWidth: '550px'
  }
}))(Tooltip);

const tooltipStyles = {
  tooltipLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontFamily: 'Helvetica',
    fontSize: '12px',
    fontWeight: 300,
    margin: '5px 8px'
  },
  date: {
    fontWeight: 400,
    marginLeft: '7px'
  }
};

/**
 * Component to render a Tableau report view
 */
const Report = ({ clientId, ...rest }: ReportProps) => {
  const history = useHistory();
  const { reportId } = useParams<{ reportId: string }>();
  const { loading, report, lastUpdateDates, documents, refreshLastUpdate } = useReports(clientId, reportId);
  const [viz, setViz] = React.useState<any>(null);
  const [isInteractive, setIsInteractive] = React.useState<boolean>(false);
  const clientRef = React.useRef<number>(clientId);
  const { fetchTableauToken } = useTableau(clientId);
  const [tableauToken, setTableauToken] = React.useState<TableauToken | undefined>(undefined);
  const [fetchingToken, setFetchingToken] = React.useState<boolean>(true);

  const options = {
    width: '100%',
    height: '100%',
    hideToolbar: 'true',
    onFirstInteractive: () => setIsInteractive(true)
  };

  React.useEffect(() => {
    fetchTableauToken()
      .then((response) => {
        setTableauToken(response);
      })
      .finally(() => setFetchingToken(false));
  }, []);

  React.useEffect(() => {
    /** Redirect home if user selects different client */
    if (clientRef.current != clientId) {
      history.replace(Paths.Home, { redirect: true });
    }
  }, [clientId]);

  const checkInteractive = () => {
    setIsInteractive(true);
  };

  React.useEffect(() => {
    if (viz) {
      viz.addEventListener('firstinteractive', checkInteractive);
    }
  }, [viz]);

  function renderDates() {
    return (
      lastUpdateDates.length > 0 &&
      lastUpdateDates.map((item, index) => (
        <div key={`last-update-${index}`} style={tooltipStyles.tooltipLine}>
          <span>{item.Label}: </span>
          <span style={tooltipStyles.date}>{item.LastUpdated}</span>
        </div>
      ))
    );
  }

  const urlText = report?.reportContentViewModels[0].children[0].desktop;

  const breadcrumbs: Breadcrumb[] = [{ title: BreadcrumbOptions.Dashboard, path: Paths.Reports }];

  function renderToolbar() {
    if (!report) {
      return null;
    }

    return (
      <React.Fragment>
        <Breadcrumbs breadcrumbs={breadcrumbs} currentPage={report.name} />
        <div className={styles.rightHeader}>
          <div onClick={() => refreshLastUpdate()}>
            <TableauButton viz={viz} dashboardName={report.name} type={TableauTool.Refresh} edge="end" />
          </div>
          {lastUpdateDates && lastUpdateDates.length > 0 && (
            <React.Fragment>
              <span className={styles.headerText}>
                Last update:{' '}
                <span className={styles.date}>{lastUpdateDates.length > 0 && lastUpdateDates[0].LastUpdated}</span>
              </span>
              <InfoTooltip title={renderDates()}>
                <InfoIcon htmlColor="#006da8" />
              </InfoTooltip>
            </React.Fragment>
          )}
        </div>
      </React.Fragment>
    );
  }

  return (
    <div className={styles.report}>
      <div className={styles.header}>
        {loading || fetchingToken ? (
          <div className={styles.loading}>
            <CircularProgress />
          </div>
        ) : (
          renderToolbar()
        )}
      </div>
      {!loading && tableauToken?.token && report && (
        <TableauReport
          documents={documents}
          url={urlText}
          options={options}
          viz={viz}
          setViz={setViz}
          isInteractive={isInteractive}
          reportMeta={report}
          tableauToken={tableauToken}
        />
      )}
    </div>
  );
};

export default Report;
